import useSWRMutation from "swr/mutation";
import { useAccountId } from "../../../../auth/useAccountId";
import { CORE_API_HOSTNAME } from "../../../../env";
import fulfill from "../../../../fulfill";

export type SearchedCompany = {
    organization_number: string;
    business_name: string;
    website: string;
    address: {
        address_line?: string;
        address_line_2?: string;
        postal_code?: string;
        postal_place?: string;
        country?: string;
    };
};

const asProtocolUrl = (url: string | undefined) => {
    if (!url) {
        return "";
    }
    if (url.startsWith("http")) {
        return url;
    }
    return `https://${url}`;
};

const fetchCompanySearch = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            partialName: string;
        };
    },
) => {
    const partialName = options.arg.partialName;
    let searchProp = "name";
    let searchValue = partialName;
    const possibleOrgnumber = partialName.replace(/\D/g, "");
    if (possibleOrgnumber.length === 9) {
        searchValue = possibleOrgnumber;
        searchProp = "organization_number";
    }
    searchValue = encodeURIComponent(searchValue);
    const response: SearchedCompany[] = await fulfill.get({
        url: `${CORE_API_HOSTNAME}/v1/accounts/${options.arg.accountId}/search/external/organizations/no?${searchProp}=${searchValue}`,
        accountId: "none",
        handlers: {
            200: (response) => {
                const accounts = response.map((entry: any) => {
                    return {
                        organization_number: entry.organization_number,
                        business_name: entry.business_name,
                        website: asProtocolUrl(entry.url),
                        address: {
                            address_line: entry.business_address?.address_line,
                            address_line_2:
                                entry.business_address?.address_line_2,
                            postal_code: entry.business_address?.postal_code,
                            postal_place: entry.business_address?.postal_place,
                            country: entry.business_address?.country,
                        },
                    };
                });
                return accounts;
            },
        },
    });
    return response;
};

export const useCompanySearch = () => {
    const accountId = useAccountId();
    const { trigger, isMutating, error, data } = useSWRMutation(
        () =>
            accountId ? `${accountId}/search/external/organizations/no` : null,
        fetchCompanySearch,
    );

    const searchCompany = (partialName: string) => {
        trigger({ accountId, partialName });
    };

    return {
        searchCompany,
        isSearchingCompany: isMutating,
        searchCompanyError: error,
        searchedCompanies: data || [],
    };
};
